import React, { useCallback, useEffect, useState } from 'react';
import { Gauge, Loader2, AlertCircle } from 'lucide-react';
import { previewServiceDueApi } from '../../services/bookingApi';

/**
 * The odometer reading, and what it makes due.
 *
 * ## Why it previews before anything is booked
 *
 * The work — and therefore the price — is CALCULATED here rather than chosen, so a
 * service advisor has to be able to see the answer and check it against the vehicle's
 * history before quoting a customer on it. The panel shows the baseline it measured
 * from for exactly that reason: a reading typed as 950,000 instead of 95,000 is obvious
 * beside "last serviced at 70,000 km", and invisible on its own.
 *
 * ## A vehicle with no history
 *
 * ⚠️ A brand-new vehicle has no uuid yet, and one this workshop has never serviced has
 * no baseline. Both come to the same answer — the routine service — and the panel says
 * so without calling the server for the first, because there is nothing to look up.
 */

/** Why a service came out due. The server sends the reason; this is how it reads. */
const REASON_LABEL = {
    DISTANCE: 'distance since last service',
    TIME: 'time since last service',
    FIRST_SERVICE: 'first service with this workshop',
};

const formatKm = (km) => (km === null || km === undefined ? null : `${Number(km).toLocaleString()} km`);

const MileageServicePanel = ({ vehicleUuid, odometerKm, onOdometerChange }) => {
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const isNewVehicle = !vehicleUuid;
    const reading = Number(odometerKm);
    const hasReading = odometerKm !== '' && odometerKm !== null && odometerKm !== undefined
        && Number.isFinite(reading) && reading >= 0;

    const load = useCallback(async () => {
        // Nothing to look up for a vehicle that does not exist yet.
        if (isNewVehicle || !hasReading) {
            setPreview(null);
            setError(null);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            setPreview(await previewServiceDueApi({ vehicleUuid, odometerKm: reading }));
        } catch (e) {
            setError(e.response?.data?.message ?? 'Could not work out what is due.');
            setPreview(null);
        } finally {
            setLoading(false);
        }
    }, [vehicleUuid, reading, hasReading, isNewVehicle]);

    // Debounced: the reading is typed a digit at a time, and each keystroke would
    // otherwise be a request — with the answers arriving out of order.
    useEffect(() => {
        const timer = setTimeout(load, 400);
        return () => clearTimeout(timer);
    }, [load]);

    return (
        <div className="space-y-2.5 pt-1">
            <label className="block">
                <span className="text-xs font-medium text-gray-600">Odometer reading</span>
                <div className="relative mt-1">
                    <Gauge size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    <input
                        type="number"
                        min="0"
                        max="2000000"
                        inputMode="numeric"
                        value={odometerKm ?? ''}
                        onChange={(e) => onOdometerChange(e.target.value)}
                        placeholder="e.g. 95000"
                        aria-label="Odometer reading"
                        className="w-full pl-8 pr-10 py-2 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">km</span>
                </div>
            </label>

            {isNewVehicle && hasReading && (
                <p className="text-xs text-gray-500">
                    No service history for a new vehicle — the routine service will be scheduled,
                    and the history starts from this visit.
                </p>
            )}

            {loading && (
                <div className="flex items-center gap-2 py-3 text-gray-400 text-sm">
                    <Loader2 size={14} className="animate-spin" /> Working out what is due…
                </div>
            )}

            {error && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5">
                    <AlertCircle size={14} className="mt-0.5 shrink-0 text-amber-600" />
                    <p className="text-xs text-amber-900">{error}</p>
                </div>
            )}

            {preview && !loading && (
                <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-2.5">
                    {/* ⚠️ The baseline is shown, not just the answer. A mistyped reading is
                        obvious beside the last one and invisible on its own. */}
                    <p className="text-xs text-gray-500">
                        {preview.lastServiceOdometerKm === null || preview.lastServiceOdometerKm === undefined
                            ? 'No previous service on record for this vehicle.'
                            : `Last serviced at ${formatKm(preview.lastServiceOdometerKm)}`}
                        {preview.kmSinceLastService !== null && preview.kmSinceLastService !== undefined
                            && ` · ${formatKm(preview.kmSinceLastService)} since`}
                    </p>

                    {preview.due.length === 0 ? (
                        <p className="text-sm text-gray-600">
                            Nothing is due at this reading. The booking can still go ahead — work
                            items can be added by hand.
                        </p>
                    ) : (
                        <>
                            <ul className="space-y-1">
                                {preview.due.map((service) => (
                                    <li key={service.uuid} className="flex items-baseline justify-between gap-3">
                                        <span className="text-sm font-medium text-gray-900">{service.name}</span>
                                        <span className="text-[11px] text-gray-400 whitespace-nowrap">
                                            {REASON_LABEL[service.reason] ?? service.reason}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                            <p className="text-xs text-gray-500 border-t border-gray-100 pt-2">
                                {preview.workItems.length} work item{preview.workItems.length === 1 ? '' : 's'} after
                                merging — where two services ask for the same job, the more thorough one is kept.
                            </p>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default MileageServicePanel;
